import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAccessToken } from "@/hooks";
import { SeasonsApi } from "@/openapi/generated";
import { QueryKeys } from "@/lib/types";
import { createAuthenticatedApi, getApiErrorMessage } from "@/lib/utils";
import { useToast } from "@/components/feedback";

export const useSeasonBulkDelete = () => {
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();
  const toast = useToast();

  const { mutate, isPending } = useMutation({
    mutationFn: async (seasonIds: number[]) => {
      if (!accessToken) {
        throw new Error("Authentication token is required");
      }
      const service = createAuthenticatedApi(SeasonsApi, accessToken);
      await Promise.all(
        seasonIds.map((id) => service.apiSeasonsIdDelete({ id })),
      );
    },
    onSuccess: async (_data, seasonIds) => {
      for (const seasonId of seasonIds) {
        queryClient.removeQueries({ queryKey: [QueryKeys.Seasons, seasonId] });
      }
      await queryClient.invalidateQueries({ queryKey: [QueryKeys.Seasons] });
      const count = seasonIds.length;
      toast({
        title: count === 1 ? "Season deleted" : "Seasons deleted",
        description:
          count === 1
            ? "Season has been deleted successfully!"
            : `${count} seasons deleted successfully!`,
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    },
    onError: async (error: unknown) => {
      const errorMessage = await getApiErrorMessage(
        error,
        "Failed to delete seasons. Seasons with associated classes cannot be deleted.",
      );
      toast({
        title: "Error",
        description: errorMessage,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    },
  });

  return { mutate, isPending };
};
